<template>
  <div>
    <!-- Chart title -->
    <h4 v-if="title">{{ title }}</h4>
    <!-- Period select -->
    <div v-if="extra.periodSelect && extra.periodSelectTop" class="row">
      <!-- Single date mode -->
      <div class="col">
        <div class="input-group mx-auto justify-content-center">
          <!-- Previous date -->
          <button type="button" class="btn btn-primary" @click="setDiff(-1)">
            <i class="fas fa-arrow-left"></i>
          </button>
          <!-- Selected date -->
          <input type="date" class="form-control" v-model="selectedDate" :max="date" @change="requestChart" style="max-width: 200px;" />
          <!-- Next date -->
          <button v-if="date !== selectedDate" type="button" class="btn btn-primary" @click="setDiff(1)">
            <i class="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>
    <!-- Chart -->
    <div v-for="(chart, index) in chartOptions" :key="index" class="d-flex flex-row py-1" :class="{'bg-light': index % 2}">
      <!-- Extras left -->
      <div class="px-2 text-center align-self-center" v-if="'extras' in chart && 'left' in chart.extras && chart.extras.left.length > 0">
        <div v-for="(extra, index) in chart.extras.left" :key="index">
          <h2 class="m-0" v-if="index === 0">{{ extra }}</h2>
          <span v-if="index > 0">{{ extra }}</span>
        </div>
      </div>
      <!-- Chart -->
      <div class="flex-grow-1 chart-container">
        <apexchart :height="extra.height" :options="chart" :series="chart.series"></apexchart>
      </div>
      <!-- Extras right -->
      <div class="px-2 text-center align-self-center" v-if="'extras' in chart && 'right' in chart.extras && chart.extras.right.length > 0">
        <div v-for="(extra, index) in chart.extras.right" :key="index">
          <div v-if="index === 0">{{ extra }}</div>
          <small v-if="index > 0">{{ extra }}</small>
        </div>
      </div>
    </div>
    <!-- Loading spinner -->
    <div v-if="chartOptions.length === 0" class="d-flex justify-content-center" :style="'height:' + extra.height + 'px'">
      <div class="spinner-border text-primary align-middle" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <!-- Period select -->
    <div v-if="extra.periodSelect && extra.periodSelectBottom" class="row">
      <!-- Single date mode -->
      <div class="col">
        <div class="input-group mx-auto justify-content-center">
          <!-- Previous date -->
          <button type="button" class="btn btn-primary" @click="setDiff(-1)">
            <i class="fas fa-arrow-left"></i>
          </button>
          <!-- Selected date -->
          <input type="date" class="form-control" v-model="selectedDate" :max="date" @change="requestChart" style="max-width: 200px;" />
          <!-- Next date -->
          <button v-if="date !== selectedDate" type="button" class="btn btn-primary" @click="setDiff(1)">
            <i class="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ['title', 'extra'],
  data () {
    return {
      chartOptions: [],
      calendar: false,
      date: null,
      selectedDate: null,
      startDate: null,
      endDate: null,
      minUpdateTime: 300000,
      maxUpdateTime: 900000,
      lastUpdateTime: 0,
      autoUpdateInterval: null
    }
  },
  sockets: {
    getChart: function (data) {
      if (data.reqData.id !== undefined && data.reqData.id === this.extra.id) {
        if (Array.isArray(data.chartOptions)) {
          this.chartOptions.push(...data.chartOptions)
        } else {
          this.chartOptions.push(data.chartOptions)
        }
        // console.log('chartOptions now:', this.chartOptions)
        this.lastUpdateTime = Date.now()
      }
    }
  },
  watch: {
    extra: function () {
      this.chartOptions = []
      this.requestChart()
    }
  },
  created () {
    const date = new Date()
    this.date = date.getFullYear() + '-' + (date.getMonth() + 1).toString().padStart(2, '0') + '-' + date.getDate().toString().padStart(2, '0')
    this.selectedDate = this.date
    this.startDate = this.date
    this.endDate = this.date
    this.requestChart()

    // Modify local variables if alternatives available in extra
    if (this.extra.minUpdateTime) { this.minUpdateTime = this.extra.minUpdateTime }
    if (this.extra.maxUpdateTime) { this.maxUpdateTime = this.extra.maxUpdateTime }

    // Start auto updater interval if maxUpdateTime is set in extra
    if (this.extra.maxUpdateTime) {
      this.autoUpdateInterval = setInterval(() => this.autoUpdate, 5000)
    }

    // TODO test if dynamic watchers are ok in created()
    // Setup watchers dynamically to call chart update method
    const _this = this
    this.extra.chartOptions.series.forEach((s) => {
      // console.log('Setting dynamic watcher for ' + s.name)
      _this.$watch(() => this.$store.getters.device(_this.extra.serial)[s.name], _this.chartValueUpdated)
    })
  },
  beforeDestroy () {
    this.chartOptions = []
    clearInterval(this.autoUpdateInterval)
  },
  methods: {
    // Automatic chart update if maxUpdateTime has elapsed with no updates
    autoUpdate () {
      if (Date.now() - this.lastUpdateTime > this.maxUpdateTime && this.date === this.selectedDate) {
        // console.log('Requesting automatic chart update as ' + Date.now() - this.lastUpdateTime + 'ms has passed since last update ant thresold is set to ' + this.maxUpdateTime + 'ms')
        this.requestChart()
      }
    },
    // A value used on chart was updated in store, lets see if we can refresh the chart
    chartValueUpdated () {
      // console.log('A value used on chart was updated in store!')
      // console.log('Time now:', Date.now())
      // console.log('Last update:', this.lastUpdateTime)
      // console.log('Difference:', Date.now() - this.lastUpdateTime)
      // console.log('Min. difference:', this.minUpdateTime)

      if (Date.now() > this.minUpdateTime + this.lastUpdateTime && this.date === this.selectedDate) {
        this.requestChart()
      }
    },
    // Move date forward/backward
    setDiff (mod) {
      const selectedDateObject = new Date(Date.parse(this.selectedDate))
      if (mod === 1) {
        selectedDateObject.setDate(selectedDateObject.getDate() + 1)
      } else {
        selectedDateObject.setDate(selectedDateObject.getDate() - 1)
      }
      this.selectedDate = selectedDateObject.getFullYear() + '-' + (selectedDateObject.getMonth() + 1).toString().padStart(2, '0') + '-' + selectedDateObject.getDate().toString().padStart(2, '0')
      this.startDate = this.selectedDate
      this.endDate = this.selectedDate
      this.requestChart()
    },
    // Request chart from server
    requestChart () {
      // console.log('Sent getChart:', { serial: this.extra.serial, id: this.extra.id, startDate: this.selectedDate, endDate: this.selectedDate, chartOptions: this.extra.chartOptions })
      this.$socket.emit('getChart', { serial: this.extra.serial, id: this.extra.id, startDate: this.startDate, endDate: this.endDate, chartOptions: this.extra.chartOptions })
      this.chartOptions = []
    }
  }
}
</script>

<style>
.chart-container > div {
  min-height: 20px !important;
  margin: 0 !important;
  padding: 0 !important;
}
</style>
